import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";

// Constants for pagination
const MESSAGES_PER_PAGE = 50;
const MAX_MESSAGE_LENGTH = 1000; // Characters before creating new chat session

// Chat collection structure:
// chats/{chatId} - Main chat document
// chats/{chatId}/messages/{messageId} - Individual messages

export const chatModel = {
  // Create a new chat session
  async createChat(tenantId, landlordId, tenantInfo) {
    try {
      const chatData = {
        tenantId,
        landlordId,
        tenantName: tenantInfo.name,
        tenantEmail: tenantInfo.email,
        unitNumber: tenantInfo.unitNumber,
        status: "active",
        leoEnabled: true, // Leo is enabled by default
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: "",
        lastMessageTime: serverTimestamp(),
        unreadCount: 0,
        messageCount: 0,
        currentSessionId: null, // Will be set when first message is sent
      };

      const chatRef = await addDoc(collection(db, "chats"), chatData);

      // Create initial AI message
      await this.addMessage(
        chatRef.id,
        "ai",
        "Hello! I'm Clumse AI. I'm here to help you with any questions about your rental property, maintenance requests, or general inquiries. How can I assist you today?"
      );

      return chatRef.id;
    } catch (error) {
      console.error("Error creating chat:", error);
      throw error;
    }
  },

  // Get or create chat for tenant
  // This returns the REGULAR tenant chat (not maintenance group chats)
  async getOrCreateChat(tenantId, landlordId, tenantInfo) {
    try {
      // First, try to find existing active chat that is NOT a maintenance group chat
      // Maintenance chats have: type === 'maintenance_group' OR handymanIds array
      const chatsQuery = query(
        collection(db, "chats"),
        where("tenantId", "==", tenantId),
        where("landlordId", "==", landlordId),
        where("status", "==", "active"),
        orderBy("updatedAt", "desc")
      );

      const chatsSnapshot = await getDocs(chatsQuery);

      if (!chatsSnapshot.empty) {
        // Filter out maintenance group chats
        // A chat is a maintenance chat if it has type === 'maintenance_group' OR handymanIds array
        const regularChats = chatsSnapshot.docs.filter((doc) => {
          const data = doc.data();
          const isMaintenanceChat =
            data.type === "maintenance_group" ||
            (data.handymanIds &&
              Array.isArray(data.handymanIds) &&
              data.handymanIds.length > 0) ||
            (data.ticketId && data.ticketId !== null && data.ticketId !== "");
          return !isMaintenanceChat;
        });

        if (regularChats.length > 0) {
          // Return the most recently updated regular chat
          return regularChats[0].id;
        }
      }

      // If no active regular chat exists, create a new one
      return await this.createChat(tenantId, landlordId, tenantInfo);
    } catch (error) {
      console.error("Error getting or creating chat:", error);
      throw error;
    }
  },

  // Add a message to a chat
  async addMessage(chatId, sender, content, metadata = {}, images = []) {
    try {
      const messageData = {
        sender, // 'tenant', 'landlord', or 'ai'
        content,
        images: images || [], // Array of image URLs
        timestamp: serverTimestamp(),
        metadata: {
          ...metadata,
          messageLength: content.length,
          hasImages: images && images.length > 0,
          imageCount: images ? images.length : 0,
        },
      };

      // Add message to messages subcollection
      const messageRef = await addDoc(
        collection(db, "chats", chatId, "messages"),
        messageData
      );

      // Update chat document with latest message info
      const lastMessageText =
        images && images.length > 0
          ? `${content} ${
              images.length > 0
                ? `[${images.length} image${images.length > 1 ? "s" : ""}]`
                : ""
            }`
          : content;

      await updateDoc(doc(db, "chats", chatId), {
        lastMessage: lastMessageText,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messageCount: await this.getMessageCount(chatId),
      });

      // Check if we need to create a new session due to message length
      await this.checkAndCreateNewSession(chatId);

      return messageRef.id;
    } catch (error) {
      console.error("Error adding message:", error);
      throw error;
    }
  },

  // Get messages for a chat with pagination
  async getMessages(
    chatId,
    lastMessageId = null,
    limitCount = MESSAGES_PER_PAGE
  ) {
    try {
      let messagesQuery = query(
        collection(db, "chats", chatId, "messages"),
        orderBy("timestamp", "desc"),
        limit(limitCount)
      );

      // If we have a lastMessageId, start after that message
      if (lastMessageId) {
        const lastMessageDoc = await getDoc(
          doc(db, "chats", chatId, "messages", lastMessageId)
        );
        if (lastMessageDoc.exists()) {
          messagesQuery = query(
            collection(db, "chats", chatId, "messages"),
            orderBy("timestamp", "desc"),
            startAfter(lastMessageDoc),
            limit(limitCount)
          );
        }
      }

      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(),
      }));

      return messages.reverse(); // Return in chronological order
    } catch (error) {
      console.error("Error getting messages:", error);
      throw error;
    }
  },

  // Get all chats for a landlord
  async getLandlordChats(landlordId) {
    try {
      // Try with orderBy first, fallback to without if index is missing
      let chatsSnapshot;
      let usedOrderBy = true;

      try {
        const chatsQuery = query(
          collection(db, "chats"),
          where("landlordId", "==", landlordId),
          orderBy("updatedAt", "desc")
        );
        chatsSnapshot = await getDocs(chatsQuery);
      } catch (orderByError) {
        // If orderBy fails (missing index), try without it
        console.warn("orderBy failed, trying without orderBy:", orderByError);
        usedOrderBy = false;
        const chatsQuery = query(
          collection(db, "chats"),
          where("landlordId", "==", landlordId)
        );
        chatsSnapshot = await getDocs(chatsQuery);
      }

      const chats = chatsSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt || new Date(),
          lastMessageTime:
            data.lastMessageTime?.toDate?.() || data.lastMessageTime || null,
        };
      });

      // Sort manually if we didn't use orderBy
      if (!usedOrderBy) {
        chats.sort((a, b) => {
          const aTime =
            a.updatedAt?.getTime?.() || new Date(a.updatedAt).getTime() || 0;
          const bTime =
            b.updatedAt?.getTime?.() || new Date(b.updatedAt).getTime() || 0;
          return bTime - aTime;
        });
      }

      const maintenanceChats = chats.filter(
        (c) => c.type === "maintenance_group"
      );
      console.log(
        `[getLandlordChats] Loaded ${chats.length} total chats for landlord ${landlordId}`
      );
      console.log(
        `[getLandlordChats] Found ${maintenanceChats.length} maintenance_group chats:`,
        maintenanceChats.map((c) => ({
          id: c.id,
          type: c.type,
          status: c.status,
          ticketId: c.ticketId,
        }))
      );

      // Debug: Log all chat types to see what we're actually getting
      const chatTypes = [...new Set(chats.map((c) => c.type || "undefined"))];
      console.log(`[getLandlordChats] All chat types found:`, chatTypes);

      // Log full chat objects to see all fields
      console.log(
        `[getLandlordChats] Full chat data (first 3):`,
        chats.slice(0, 3)
      );

      // Check for chats with handymanIds or ticketId
      const chatsWithHandymanIds = chats.filter(
        (c) =>
          c.handymanIds &&
          Array.isArray(c.handymanIds) &&
          c.handymanIds.length > 0
      );
      const chatsWithTicketId = chats.filter(
        (c) => c.ticketId && c.ticketId !== null && c.ticketId !== ""
      );
      console.log(
        `[getLandlordChats] Chats with handymanIds:`,
        chatsWithHandymanIds.length,
        chatsWithHandymanIds.map((c) => ({
          id: c.id,
          handymanIds: c.handymanIds,
        }))
      );
      console.log(
        `[getLandlordChats] Chats with ticketId:`,
        chatsWithTicketId.length,
        chatsWithTicketId.map((c) => ({ id: c.id, ticketId: c.ticketId }))
      );

      // Log all field names from first chat
      if (chats.length > 0) {
        console.log(
          `[getLandlordChats] All fields in first chat:`,
          Object.keys(chats[0])
        );
      }

      return chats;
    } catch (error) {
      console.error("Error getting landlord chats:", error);
      throw error;
    }
  },

  // Get chat by ID
  async getChat(chatId) {
    try {
      const chatDoc = await getDoc(doc(db, "chats", chatId));
      if (chatDoc.exists()) {
        return {
          id: chatDoc.id,
          ...chatDoc.data(),
          createdAt: chatDoc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: chatDoc.data().updatedAt?.toDate?.() || new Date(),
          lastMessageTime:
            chatDoc.data().lastMessageTime?.toDate?.() || new Date(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting chat:", error);
      throw error;
    }
  },

  // Update chat settings
  async updateChat(chatId, updates) {
    try {
      await updateDoc(doc(db, "chats", chatId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error("Error updating chat:", error);
      throw error;
    }
  },

  // Mark messages as read
  async markMessagesAsRead(chatId, userId) {
    try {
      await updateDoc(doc(db, "chats", chatId), {
        unreadCount: 0,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
      throw error;
    }
  },

  // Get message count for a chat
  async getMessageCount(chatId) {
    try {
      const messagesQuery = query(collection(db, "chats", chatId, "messages"));
      const messagesSnapshot = await getDocs(messagesQuery);
      return messagesSnapshot.size;
    } catch (error) {
      console.error("Error getting message count:", error);
      return 0;
    }
  },

  // Check if we need to create a new session based on message length
  async checkAndCreateNewSession(chatId) {
    try {
      const chatDoc = await getDoc(doc(db, "chats", chatId));
      if (!chatDoc.exists()) return;

      const chatData = chatDoc.data();
      const totalMessages = await this.getMessageCount(chatId);

      // If we have too many messages, create a new session
      if (totalMessages > MAX_MESSAGE_LENGTH) {
        await this.createNewSession(chatId);
      }
    } catch (error) {
      console.error("Error checking session length:", error);
    }
  },

  // Create a new session for a chat (for pagination)
  async createNewSession(chatId) {
    try {
      const sessionId = `session_${Date.now()}`;

      await updateDoc(doc(db, "chats", chatId), {
        currentSessionId: sessionId,
        sessionCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Add a session marker message
      await this.addMessage(
        chatId,
        "system",
        "New conversation session started",
        {
          sessionId,
          type: "session_marker",
        }
      );

      return sessionId;
    } catch (error) {
      console.error("Error creating new session:", error);
      throw error;
    }
  },

  // Real-time listener for messages
  subscribeToMessages(chatId, callback) {
    const messagesQuery = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("timestamp", "desc"),
      limit(50) // Only listen to recent messages for real-time updates
    );

    return onSnapshot(messagesQuery, (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(),
      }));

      callback(messages.reverse()); // Return in chronological order
    });
  },

  // Real-time listener for chat updates
  subscribeToChatUpdates(landlordId, callback) {
    const chatsQuery = query(
      collection(db, "chats"),
      where("landlordId", "==", landlordId),
      orderBy("updatedAt", "desc")
    );

    return onSnapshot(chatsQuery, (snapshot) => {
      const chats = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        lastMessageTime: doc.data().lastMessageTime?.toDate?.() || new Date(),
      }));

      callback(chats);
    });
  },

  // Generate AI response (placeholder - integrate with your AI service)
  async generateAIResponse(userMessage, chatContext) {
    // This is a placeholder - integrate with your actual AI service
    const message = userMessage.toLowerCase();

    if (message.includes("rent") || message.includes("payment")) {
      return `Your rent is $${
        chatContext.monthlyRent || 1200
      } and is due on the 1st of each month. You can pay through the tenant portal or contact your landlord for alternative payment methods.`;
    } else if (
      message.includes("maintenance") ||
      message.includes("repair") ||
      message.includes("fix")
    ) {
      return `I can help you submit a maintenance request. Please describe the issue in detail, and I'll create a request for your landlord. What needs to be repaired?`;
    } else if (message.includes("lease") || message.includes("contract")) {
      return `For lease-related questions, I recommend contacting your landlord directly. I can help you with general property questions or maintenance requests.`;
    } else if (
      message.includes("hello") ||
      message.includes("hi") ||
      message.includes("hey")
    ) {
      return `Hello! I'm Clumse AI. I'm here to help you with property-related questions, maintenance requests, and general inquiries. What can I help you with today?`;
    } else {
      return `I understand you're asking about "${userMessage}". I'm here to help with property-related questions, maintenance requests, rent payments, and general inquiries. Could you provide more details about what you need help with?`;
    }
  },

  // Send message and get AI response
  async sendMessageWithAIResponse(chatId, sender, content, chatContext) {
    // Deprecated: prefer sendTenantMessageViaBackend
    try {
      await this.addMessage(chatId, sender, content);
      return "";
    } catch (error) {
      console.error("Error in legacy sendMessageWithAIResponse:", error);
      throw error;
    }
  },

  // Backend conversation helpers and send flow
  CONV_KEY(tenantId) {
    return `conv-${tenantId}`;
  },

  async getOrCreateConversation(
    tenantId,
    landlordId,
    unitId = undefined,
    propertyId = undefined
  ) {
    try {
      const key = this.CONV_KEY(tenantId);
      const cached =
        typeof window !== "undefined" ? localStorage.getItem(key) : null;
      if (cached) return cached;

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error("REACT_APP_API_URL not set");
      const createUrl = `${apiUrl}/conversations/create`;
      const payload = { tenantId, landlordId };
      if (typeof unitId === "string" && unitId.trim())
        payload.unitId = unitId.trim();
      if (typeof propertyId === "string" && propertyId.trim())
        payload.propertyId = propertyId.trim();
      console.log(
        "[TenantChat] POST /conversations/create",
        createUrl,
        payload
      );
      const res = await fetch(createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      console.log("[TenantChat] /create response", res.status, data);
      if (!res.ok)
        throw new Error(
          `create conversation failed ${res.status} ${data?.error || ""}`
        );
      if (typeof window !== "undefined")
        localStorage.setItem(key, data.conversationId);
      return data.conversationId;
    } catch (error) {
      console.error("Error getOrCreateConversation:", error);
      throw error;
    }
  },

  async sendTenantMessageViaBackend({
    tenantId,
    landlordId,
    message,
    images = [],
    unitId = undefined,
    propertyId = undefined,
    isForLeo = false,
  }) {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) throw new Error("REACT_APP_API_URL not set");
    const conversationId = await this.getOrCreateConversation(
      tenantId,
      landlordId,
      unitId,
      propertyId
    );
    console.log("[chatModel] Using conversationId", conversationId);

    const sendUrl = `${apiUrl}/conversations/send-message`;
    const body = {
      conversationId,
      fromUserId: tenantId,
      fromUserType: "tenant",
      message,
      images,
      isForLeo,
    };
    console.log("[TenantChat] POST /conversations/send-message", {
      conversationId,
      tenantId,
      messageLength: message.length,
      imageCount: images.length,
      isForLeo,
      fullBody: body,
    });
    const res = await fetch(sendUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    console.log("[TenantChat] send-message response", {
      status: res.status,
      success: data.success,
      messageId: data.messageId,
      conversationId: data.conversationId,
      fullResponse: data,
    });
    if (!res.ok)
      throw new Error(`send-message failed ${res.status} ${data?.error || ""}`);
    return data; // { success, messageId, aiResponse?: { messageId, text, repairTicketId, actions } }
  },

  /**
   * Create or get a maintenance group chat with tenant, landlord, and handyman(s)
   * @param {Object} params - Chat creation parameters
   * @param {string} params.tenantId - Tenant ID
   * @param {string} params.landlordId - Landlord ID
   * @param {string|string[]} params.handymanIds - Single handyman ID or array of handyman IDs
   * @param {string} params.ticketId - Optional maintenance ticket ID
   * @param {string} params.ticketTitle - Optional ticket title for chat name
   * @param {string} params.unitNumber - Optional unit number for chat name
   * @param {string} params.tenantName - Tenant name
   * @returns {Promise<string>} Chat ID
   */
  async createOrGetMaintenanceGroupChat({
    tenantId,
    landlordId,
    handymanIds,
    ticketId,
    ticketTitle,
    unitNumber,
    tenantName,
  }) {
    try {
      // Normalize handymanIds to array
      const handymanIdsArray = Array.isArray(handymanIds)
        ? handymanIds
        : handymanIds
        ? [handymanIds]
        : [];

      // Generate a unique chat name based on ticket/unit
      const chatName = ticketTitle
        ? `${unitNumber ? `Unit ${unitNumber} - ` : ""}${ticketTitle}`
        : `${unitNumber ? `Unit ${unitNumber} - ` : ""}Maintenance Chat`;

      // Check if a maintenance group chat already exists for this ticket
      if (ticketId) {
        const existingChatsQuery = query(
          collection(db, "chats"),
          where("tenantId", "==", tenantId),
          where("landlordId", "==", landlordId),
          where("ticketId", "==", ticketId),
          where("type", "==", "maintenance_group"),
          where("status", "==", "active"),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const existingChatsSnapshot = await getDocs(existingChatsQuery);
        if (!existingChatsSnapshot.empty) {
          const existingChat = existingChatsSnapshot.docs[0];
          const existingHandymanIds = existingChat.data().handymanIds || [];

          // Merge handyman IDs - add new ones if they don't exist
          const allHandymanIds = [
            ...new Set([...existingHandymanIds, ...handymanIdsArray]),
          ];

          // If handymen were added, update the chat
          if (allHandymanIds.length !== existingHandymanIds.length) {
            await updateDoc(doc(db, "chats", existingChat.id), {
              handymanIds: allHandymanIds,
              updatedAt: serverTimestamp(),
            });
          }

          return existingChat.id;
        }
      }

      // Create new maintenance group chat
      const chatData = {
        tenantId,
        landlordId,
        handymanIds: handymanIdsArray,
        type: "maintenance_group",
        ticketId: ticketId || null,
        chatName,
        tenantName: tenantName || "Tenant",
        unitNumber: unitNumber || null,
        status: "active",
        leoEnabled: true, // Leo is enabled by default
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: "",
        lastMessageTime: serverTimestamp(),
        unreadCount: 0,
        messageCount: 0,
      };

      const chatRef = await addDoc(collection(db, "chats"), chatData);

      // Create initial message explaining the group chat
      const initialMessage = `This is a maintenance group chat for ${
        ticketTitle || "this maintenance issue"
      }.${
        handymanIdsArray.length > 0
          ? ` Service Team member${
              handymanIdsArray.length > 1 ? "s have" : " has"
            } been added to coordinate the repair.`
          : ""
      }`;
      await this.addMessage(chatRef.id, "system", initialMessage, {
        type: "maintenance_group_created",
        ticketId: ticketId || null,
      });

      return chatRef.id;
    } catch (error) {
      console.error("Error creating maintenance group chat:", error);
      throw error;
    }
  },
};

export default chatModel;
